import {FormEvent, useCallback, useEffect, useRef, useState} from 'react';
import {t} from '../../../common/getTranslations';
import {useHasMounted} from '../../../common/shared/hooks/useHasMounted';
import {twMerge} from '../../../common/twMerge';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {Textarea} from '../../atoms/Textarea/Textarea';
import {CommentInputFieldProps} from './CommentInputFieldProps.interface';

export function CommentInputField({
  onSubmit,
  onClickEmoticonIcon,
  onClickSend,
  isEmoticonSelected,
  className,
  ...textareaProps
}: CommentInputFieldProps) {
  const {
    value = '',
    placeholder = t('comment_input'),
    maxLength = 200,
    readOnly,
  } = textareaProps;
  // count는 줄바꿈이 일어나서 textarea가 확장되었을 때에만 보여집니다.
  const [shouldShowCount, setShouldShowCount] = useState(false);
  const [initialHeightPx, setInitialHeightPx] = useState(0);
  const textareaWrapperRef = useRef<HTMLDivElement>(null);

  const hasMounted = useHasMounted();
  // 초기 텍스트가 비어있을 때의 높이값을 잡아둡니다. 줄바꿈이 일어나서 textarea 높이가 변화되면 카운트를 보여줘야 하기 때문에 필요합니다.
  useEffect(() => {
    if (!hasMounted) {
      return;
    }
    if (textareaWrapperRef.current) {
      setInitialHeightPx(textareaWrapperRef.current.clientHeight);
    }
  }, [hasMounted]);
  useEffect(() => {
    if (!textareaWrapperRef.current || !initialHeightPx) {
      return;
    }
    setShouldShowCount(
      textareaWrapperRef.current.clientHeight !== initialHeightPx
    );
  }, [value, initialHeightPx, shouldShowCount]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!!value || !!isEmoticonSelected) {
        onSubmit?.();
      }
    },
    [isEmoticonSelected, onSubmit, value]
  );

  return (
    <form
      className="flex w-full items-center px-15pxr py-10pxr"
      onSubmit={handleSubmit}
    >
      <div className="flex flex-1 items-center rounded-20pxr bg-theme-solid-10 px-16pxr py-11pxr">
        <div className="flex flex-1" ref={textareaWrapperRef}>
          <Textarea
            {...textareaProps}
            placeholder={placeholder}
            applyAutoHeight
            maxLength={maxLength}
            value={initialHeightPx ? value : '텍스트'}
            className={twMerge('h-18pxr', className)}
          />
        </div>
        <div className="ml-12pxr flex w-22pxr shrink-0 flex-col items-end self-stretch">
          {shouldShowCount && !readOnly && (
            <Text className="font-small2 flex-1 text-theme-solid-40">
              {Math.max(maxLength - String(value).length, 0)}
            </Text>
          )}
          <Icon
            className={twMerge(
              'mt-2pxr shrink-0 cursor-pointer',
              readOnly && 'cursor-not-allowed opacity-30'
            )}
            type={IconType.CommentEmojiOffStatic}
            onClick={readOnly ? undefined : onClickEmoticonIcon}
          />
        </div>
      </div>
      {(!!value || !!isEmoticonSelected) && (
        <Icon
          className="ml-10pxr h-30pxr w-30pxr cursor-pointer"
          type={IconType.CommentUploadStatic}
          onClick={readOnly ? undefined : onClickSend}
        />
      )}
    </form>
  );
}
